import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const prefersReducedMotion = () => {
    return typeof window !== 'undefined'
        && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
};

const resolveTarget = (target) => {
    if(target == null){
        return 0;
    }

    if(typeof target === 'string' || typeof target === 'number'){
        return target;
    }

    return 0;
};

const getTargetElement = (target) => {
    if(typeof target !== 'string'){
        return null;
    }

    try {
        return document.querySelector(target);
    } catch(_error){
        return null;
    }
};

const resolveScrollPosition = (target) => {
    const nextTarget = resolveTarget(target);

    if(typeof nextTarget === 'number'){
        return nextTarget;
    }

    const element = getTargetElement(nextTarget);

    if(!element){
        return 0;
    }

    if(window.gscroll && typeof window.gscroll.offset === 'function'){
        return window.gscroll.offset(element, 'top top');
    }

    return element.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
};

const SCROLL_KEYS = new Set([
    ' ',
    'ArrowDown',
    'ArrowUp',
    'End',
    'Home',
    'PageDown',
    'PageUp'
]);

export const scroller = {
    media: null,
    lockDepth: 0,
    lockedScrollTop: 0,
    activeScrollTween: null,
    smoothPauseReasons: new Set(),
    cancelScrollTween(){
        if(this.activeScrollTween){
            this.activeScrollTween.kill();
            this.activeScrollTween = null;
        }

        return this;
    },
    handleScrollInterrupt(event){
        if(event?.type === 'keydown' && !SCROLL_KEYS.has(event.key)){
            return;
        }

        scroller.cancelScrollTween();
    },
    attachScrollInterruptListeners(){
        window.addEventListener('wheel', this.handleScrollInterrupt, { passive: true });
        window.addEventListener('touchmove', this.handleScrollInterrupt, { passive: true });
        window.addEventListener('keydown', this.handleScrollInterrupt);
    },
    detachScrollInterruptListeners(){
        window.removeEventListener('wheel', this.handleScrollInterrupt);
        window.removeEventListener('touchmove', this.handleScrollInterrupt);
        window.removeEventListener('keydown', this.handleScrollInterrupt);
    },
    applySmoothPauseState(){
        window.gscroll?.paused?.(this.lockDepth > 0 || this.smoothPauseReasons.size > 0);
    },
    pauseSmooth(reason = 'manual'){
        this.smoothPauseReasons.add(reason);
        this.applySmoothPauseState();

        return this;
    },
    resumeSmooth(reason = 'manual', force = false){
        if(force){
            this.smoothPauseReasons.clear();
        } else {
            this.smoothPauseReasons.delete(reason);
        }

        this.applySmoothPauseState();

        return this;
    },
    preventScrollEvent(event){
        event.preventDefault();
    },
    preventScrollKey(event){
        if(!SCROLL_KEYS.has(event.key)){
            return;
        }

        event.preventDefault();
    },
    attachLockListeners(){
        window.addEventListener('wheel', this.preventScrollEvent, { passive: false });
        window.addEventListener('touchmove', this.preventScrollEvent, { passive: false });
        window.addEventListener('keydown', this.preventScrollKey, { passive: false });
    },
    detachLockListeners(){
        window.removeEventListener('wheel', this.preventScrollEvent);
        window.removeEventListener('touchmove', this.preventScrollEvent);
        window.removeEventListener('keydown', this.preventScrollKey);
    },
    getScrollTop(){
        if(window.gscroll && typeof window.gscroll.scrollTop === 'function'){
            return window.gscroll.scrollTop();
        }

        return window.scrollY || window.pageYOffset || 0;
    },
    restoreScrollTop(){
        if(window.gscroll && typeof window.gscroll.scrollTop === 'function'){
            window.gscroll.scrollTop(this.lockedScrollTop);
            return;
        }

        window.scrollTo({
            top: this.lockedScrollTop,
            behavior: 'auto'
        });
    },
    setLockScrollTop(value = this.getScrollTop()){
        this.lockedScrollTop = Number.isFinite(value)
            ? value
            : 0;

        return this;
    },
    setState(active){
        document.documentElement.classList.toggle('has-smooth-scroll', active);
        document.body.classList.toggle('has-smooth-scroll', active);
    },
    setLockState(active){
        document.documentElement.classList.toggle('is-scroll-locked', active);
        document.body.classList.toggle('is-scroll-locked', active);
    },
    init(){
        this.kill();

        if(prefersReducedMotion()){
            return null;
        }

        this.media = gsap.matchMedia();
        this.media.add(
            {
                isPointer: '(pointer: fine)'
            },
            (context) => {
                const { isPointer } = context.conditions;

                window.gscroll = ScrollSmoother.create({
                    wrapper: '#pageWrapper',
                    content: '#pageContent',
                    ignoreMobileResize: true,
                    normalizeScroll: Boolean(isPointer),
                    smooth: isPointer ? 1.35 : 0
                });

                this.setState(true);
                this.attachScrollInterruptListeners();
                ScrollTrigger.refresh();

                return () => {
                    this.cancelScrollTween();
                    this.detachScrollInterruptListeners();
                    this.setState(false);
                    window.gscroll?.kill();
                    window.gscroll = null;
                };
            }
        );

        return window.gscroll || null;
    },
    kill(){
        this.cancelScrollTween();
        this.detachScrollInterruptListeners();
        this.unlock(true);
        this.resumeSmooth('kill', true);
        this.setState(false);
        window.gscroll?.kill();
        window.gscroll = null;

        this.media?.revert();
        this.media = null;
    },
    refresh(){
        if(window.gscroll){
            window.gscroll.refresh();
            return;
        }

        ScrollTrigger.refresh();
    },
    scrollTo(target, smooth = true){
        const nextTarget = resolveTarget(target);

        this.cancelScrollTween();

        if(window.gscroll){
            if(!smooth){
                window.gscroll.scrollTo(nextTarget, false);
                return;
            }

            const scrollProxy = {
                value: window.gscroll.scrollTop()
            };
            const tween = gsap.to(scrollProxy, {
                value: resolveScrollPosition(nextTarget),
                duration: .85,
                ease: 'power2.out',
                overwrite: true,
                onUpdate: () => {
                    window.gscroll?.scrollTop(scrollProxy.value);
                },
                onComplete: () => {
                    if(this.activeScrollTween === tween){
                        this.activeScrollTween = null;
                    }
                },
                onInterrupt: () => {
                    if(this.activeScrollTween === tween){
                        this.activeScrollTween = null;
                    }
                }
            });

            this.activeScrollTween = tween;
            return;
        }

        if(typeof nextTarget === 'string'){
            const element = document.querySelector(nextTarget);

            if(element){
                element.scrollIntoView({
                    behavior: smooth ? 'smooth' : 'auto',
                    block: 'start'
                });
            }

            return;
        }

        window.scrollTo({
            top: nextTarget,
            behavior: smooth ? 'smooth' : 'auto'
        });
    },
    jumpToTop(){
        this.cancelScrollTween();

        if(window.gscroll){
            window.gscroll.scrollTo(0, false);
            return;
        }

        window.scrollTo({
            top: 0,
            behavior: 'auto'
        });
    },
    lock(){
        this.cancelScrollTween();

        if(this.lockDepth === 0){
            this.setLockScrollTop(this.getScrollTop());
            this.attachLockListeners();
            this.setLockState(true);
            this.restoreScrollTop();
        }

        this.lockDepth += 1;
        this.applySmoothPauseState();

        return this;
    },
    unlock(force = false){
        if(force){
            this.lockDepth = 0;
        } else if(this.lockDepth > 0){
            this.lockDepth -= 1;
        }

        if(this.lockDepth > 0){
            return this;
        }

        this.lockDepth = 0;
        this.detachLockListeners();
        this.setLockState(false);
        this.restoreScrollTop();
        this.applySmoothPauseState();

        return this;
    }
};
